// Outbound event delivery.
// Route: /settings/webhooks

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  Toggle,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDate,
  HeadersEditor,
} from "./shared";

export interface WebhookRow {
  id: string;
  name: string;
  url: string;
  events: string[];
  headers: Record<string, string>;
  is_active: boolean;
  has_secret: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookEventOption {
  value: string;
  label: string;
  description: string;
}

interface WebhookListResponse {
  webhooks: WebhookRow[];
  available_events: WebhookEventOption[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================ WebhooksView

interface WebhookForm {
  name: string;
  url: string;
  events: string[];
  secret: string;
  headers: Record<string, string>;
  is_active: boolean;
}

const EMPTY_WEBHOOK_FORM: WebhookForm = {
  name: "",
  url: "",
  events: [],
  secret: "",
  headers: {},
  is_active: true,
};

export function WebhooksView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("webhooks", "write");
  const canDeleteHook = can("webhooks", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<WebhookListResponse>(
    () => http.get<WebhookListResponse>(`/webhooks${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const webhooks = data?.webhooks ?? [];
  const events = data?.available_events ?? [];
  const total = data?.total ?? webhooks.length;

  const eventLabel = (value: string) => events.find((e) => e.value === value)?.label ?? value;

  const [editing, setEditing] = useState<WebhookRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<WebhookForm>(EMPTY_WEBHOOK_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<WebhookRow | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_WEBHOOK_FORM);
    setDialogOpen(true);
  };

  const openEdit = (webhook: WebhookRow) => {
    setEditing(webhook);
    setForm({
      name: webhook.name,
      url: webhook.url,
      events: [...webhook.events],
      secret: "",
      headers: { ...webhook.headers },
      is_active: webhook.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.push("Enter a name", "critical");
      return;
    }
    if (!form.url.trim()) {
      toast.push("Enter the delivery URL", "critical");
      return;
    }
    if (form.events.length === 0) {
      toast.push("Pick at least one event", "critical");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        url: form.url.trim(),
        events: form.events,
        headers: form.headers,
        secret: form.secret || undefined,
      };
      if (editing) {
        // PUT applies is_active unconditionally, so the current value has to
        // travel with every edit or saving would silently disable the webhook.
        await http.put(`/webhooks/${editing.id}`, { ...payload, is_active: form.is_active });
        toast.push("Webhook updated", "good");
      } else {
        await http.post("/webhooks", payload);
        toast.push("Webhook created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the webhook"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (webhook: WebhookRow) => {
    try {
      await http.put(`/webhooks/${webhook.id}`, { is_active: !webhook.is_active });
      toast.push(webhook.is_active ? "Webhook disabled" : "Webhook enabled", "good");
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not change the webhook"), "critical");
    }
  };

  const test = async (webhook: WebhookRow) => {
    setTesting(webhook.id);
    try {
      await http.post(`/webhooks/${webhook.id}/test`);
      toast.push("Test payload delivered", "good");
    } catch (e) {
      toast.push(errMessage(e, "The receiver rejected the test payload"), "critical");
    } finally {
      setTesting(null);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/webhooks/${toDelete.id}`);
      toast.push("Webhook deleted", "good");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not delete the webhook"), "critical");
      throw e;
    }
  };

  const toggleEvent = (value: string, on: boolean) => {
    setForm((f) => ({
      ...f,
      events: on ? [...f.events, value] : f.events.filter((e) => e !== value),
    }));
  };

  const columns: Column<WebhookRow>[] = [
    { key: "name", header: "Name", sortable: true, render: (w) => <span className="strong">{w.name}</span> },
    {
      key: "url",
      header: "URL",
      sortable: true,
      render: (w) => (
        <span className="muted truncate" style={{ maxWidth: 200, display: "inline-block" }}>
          {w.url}
        </span>
      ),
    },
    {
      key: "events",
      header: "Events",
      render: (w) => (
        <div className="chips">
          {w.events.slice(0, 2).map((e) => (
            <Badge key={e}>{eventLabel(e)}</Badge>
          ))}
          {w.events.length > 2 && (
            <Badge title={w.events.slice(2).map(eventLabel).join(", ")}>+{w.events.length - 2}</Badge>
          )}
        </div>
      ),
    },
    {
      key: "is_active",
      header: "Status",
      sortable: true,
      render: (w) => (
        <div className="row">
          <Toggle
            checked={w.is_active}
            disabled={!canWrite}
            ariaLabel={`${w.is_active ? "Disable" : "Enable"} ${w.name}`}
            onChange={() => void toggle(w)}
          />
          <span className="muted">{w.is_active ? "Active" : "Inactive"}</span>
        </div>
      ),
    },
    { key: "created_at", header: "Created", sortable: true, render: (w) => fmtDate(w.created_at) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (w) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn icon sm ghost"
            title="Send a test payload"
            aria-label={`Send a test payload to ${w.name}`}
            disabled={testing === w.id}
            onClick={() => void test(w)}
          >
            {testing === w.id ? <span className="spinner" /> : "▷"}
          </button>
          <button
            className="btn icon sm ghost"
            title="Edit this webhook"
            aria-label={`Edit ${w.name}`}
            disabled={!canWrite}
            onClick={() => openEdit(w)}
          >
            ✎
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete this webhook"
            aria-label={`Delete ${w.name}`}
            disabled={!canDeleteHook}
            onClick={() => setToDelete(w)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Webhooks</h1>
          <div className="sub">Where we post events as they happen.</div>
        </div>
        <div className="page-actions">
          <button className="btn primary" disabled={!canWrite} onClick={openCreate}>
            + Add webhook
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the webhooks" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your webhooks</h3>
          <Search value={search} onChange={setSearch} placeholder="Search name or URL…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={webhooks}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching webhooks" : "No webhooks yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Add an endpoint and we will post every event you subscribe to, signed with its secret."
                }
                icon="⇗"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add webhook
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="webhooks"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit webhook" : "Add webhook"}
      >
        <Field label="Name" required>
          <input
            value={form.name}
            placeholder="Claims CRM"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Delivery URL" required>
          <input
            type="url"
            value={form.url}
            placeholder="https://example.com/hooks/whatsapp"
            onChange={(e) => setForm({ ...form, url: e.target.value })}
          />
        </Field>

        <div className="field" role="group" aria-label="Events">
          <div className="group-label">Events</div>
          <div className="card">
            {events.length === 0 ? (
              <span className="muted">No event catalogue was returned.</span>
            ) : (
              events.map((e) => (
                <label className="check" key={e.value} style={{ alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    checked={form.events.includes(e.value)}
                    onChange={(ev) => toggleEvent(e.value, ev.target.checked)}
                  />
                  <span>
                    <span className="strong">{e.label}</span>
                    <span className="muted" style={{ display: "block" }}>
                      {e.description}
                    </span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        <Field
          label="Signing secret"
          hint={
            editing
              ? "Leave blank to keep the stored secret. Payloads are signed with it as X-Webhook-Signature."
              : "Leave blank and one is generated for you. Payloads are signed with it as X-Webhook-Signature."
          }
        >
          <input
            type="password"
            value={form.secret}
            autoComplete="new-password"
            onChange={(e) => setForm({ ...form, secret: e.target.value })}
          />
        </Field>

        <div className="field">
          <div className="group-label">Custom headers</div>
          <HeadersEditor headers={form.headers} onChange={(h) => setForm({ ...form, headers: h })} />
        </div>

        {editing && (
          <div className="setting">
            <div className="text">
              <div className="strong">Active</div>
              <p>An inactive webhook receives nothing.</p>
            </div>
            <Toggle
              checked={form.is_active}
              ariaLabel="Webhook active"
              onChange={(v) => setForm({ ...form, is_active: v })}
            />
          </div>
        )}

        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={saving}>
            {saving && <span className="spinner" />}
            {editing ? "Update" : "Create"}
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Delete webhook"
        body={
          <>
            <strong className="strong">{toDelete?.name}</strong> stops receiving events immediately.
            This cannot be undone.
          </>
        }
      />
    </div>
  );
}
